<template>
  <article class="ftile" :style="{ '--fg': badge.fg, '--bg': badge.bg }">
    <div class="ftile__top">
      <span class="ftile__badge">{{ typeLabel }}</span>
      <button
        class="ftile__remove"
        type="button"
        title="Remover arquivo"
        aria-label="Remover arquivo"
        @click.stop="deleteFile"
      >
        <i class="mdi mdi-close"></i>
      </button>
    </div>

    <p class="ftile__name" :title="file.name">{{ file.name }}</p>

    <div class="ftile__foot">
      <span v-if="sizeLabel" class="ftile__size">{{ sizeLabel }}</span>
      <button
        v-if="file.url"
        class="ftile__dl"
        type="button"
        title="Baixar arquivo"
        aria-label="Baixar arquivo"
        @click.stop="download(file.url)"
      >
        <i class="mdi mdi-tray-arrow-down"></i>
      </button>
    </div>

    <div
      v-if="file.progress !== undefined && file.progress < 100"
      class="ftile__progress"
    >
      <div class="ftile__bar" :style="{ width: file.progress + '%' }"></div>
    </div>
  </article>
</template>

<script>
export default {
  props: {
    file: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },
  computed: {
    // Extensão a partir do mimetype ou do nome
    ext() {
      const t = this.file && this.file.type;
      if (t && t.includes("/")) {
        const sub = t.split("/")[1];
        if (sub && sub !== "octet-stream") return sub;
      }
      const name = this.file && this.file.name;
      if (name && name.includes(".")) return name.split(".").pop();
      return t || "file";
    },
    typeLabel() {
      const map = {
        "vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
        msword: "DOC",
        "vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
        "vnd.ms-powerpoint": "PPT",
        mpeg: "MP4",
        quicktime: "MOV",
        plain: "TXT",
      };
      const e = this.ext.toLowerCase();
      const label = map[e] || e;
      return label.toUpperCase().slice(0, 4);
    },
    badge() {
      const e = this.typeLabel;
      if (e === "PDF") return { bg: "#FDE7E7", fg: "#C0392B" };
      if (["DOC", "DOCX"].includes(e)) return { bg: "#E7EFFD", fg: "#2456B5" };
      if (["PPT", "PPTX"].includes(e)) return { bg: "#FDEFE1", fg: "#B5551F" };
      if (["MP4", "MOV", "WEBM", "WEBV"].includes(e))
        return { bg: "#F3E9FC", fg: "#7A28B5" };
      return { bg: "#ECEBF0", fg: "#5F5F61" };
    },
    sizeLabel() {
      const bytes = this.file && this.file.size;
      if (!bytes || bytes <= 0) return null;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
  methods: {
    download(url) {
      if (!url) return;
      window.open(url, "_blank");
    },
    deleteFile() {
      this.$emit("removeItem", this.index);
    },
  },
};
</script>

<style scoped>
.ftile {
  --fg: #5f5f61;
  --bg: #ecebf0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  min-height: 120px;
  background: #ffffff;
  border: 1px solid #eae7f0;
  border-radius: 14px;
  font-family: "Poppins", sans-serif;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.ftile:hover {
  border-color: #ded9e8;
  box-shadow: 0 14px 28px -22px rgba(33, 27, 46, 0.5);
}

.ftile__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ftile__badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 7px;
  background: var(--bg);
  color: var(--fg);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}
.ftile__remove {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #9a96a6;
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease;
}
.ftile__remove:hover {
  color: #d52323;
  background: #fdecec;
}
.ftile__remove:focus-visible {
  outline: 2px solid #d52323;
  outline-offset: 1px;
}
.ftile__remove .mdi {
  font-size: 16px;
}

.ftile__name {
  margin: 0;
  flex: 1 1 auto;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: #211b2e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.ftile__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ftile__size {
  font-size: 11.5px;
  color: #9a96a6;
  font-variant-numeric: tabular-nums;
}
.ftile__dl {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  margin-left: auto;
  border: 1px solid #eae7f0;
  border-radius: 8px;
  background: #fff;
  color: #6e6a79;
  cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease;
}
.ftile__dl:hover {
  color: #e8276f;
  border-color: #e8276f;
}
.ftile__dl:focus-visible {
  outline: 2px solid #e8276f;
  outline-offset: 2px;
}
.ftile__dl .mdi {
  font-size: 16px;
}

.ftile__progress {
  height: 4px;
  border-radius: 999px;
  background: #eae7f0;
  overflow: hidden;
}
.ftile__bar {
  height: 100%;
  border-radius: 999px;
  background: var(--fg);
  transition: width 0.3s ease;
}

@media (prefers-reduced-motion: reduce) {
  .ftile,
  .ftile__remove,
  .ftile__dl,
  .ftile__bar {
    transition: none;
  }
}
</style>
